import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RuleRow {
  readonly aclTrimRuleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly sourceModule: string;
  readonly sourceEntityType: string;
  readonly classification: string | null;
  readonly trimType: string;
  readonly requiredPermission: string | null;
  readonly enforcement: string;
  readonly priority: number;
  readonly isActive: boolean;
}

const TRIM_TYPES = ['PERMISSION', 'OWNERSHIP', 'SCOPE', 'CLASSIFICATION', 'CUSTOM'];
const ENFORCEMENTS = ['BLOCK', 'WARN'];

/**
 * M39 — ACL-Trim Rules Console (GR_M36-M40 §G / gap O10 / §Q4). The admin surface
 * over the query-time ACL-trim rule master (T522A): security officers author the
 * rules that guarantee a low-privilege user can NEVER retrieve a document a
 * high-privilege user can. Each rule targets a source module + entity type and a
 * trim strategy (PERMISSION / OWNERSHIP / SCOPE / CLASSIFICATION / CUSTOM DSL),
 * with BLOCK (remove the hit) or WARN (keep + annotate) enforcement and a
 * priority. Viewing needs ACL_TRIM_VIEW; authoring / deleting needs
 * ACL_TRIM_MANAGE (SoD). Tenant isolation is enforced server-side (LAW 4).
 */
export function AclTrimRulesConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [sourceModule, setSourceModule] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [ruleCode, setRuleCode] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [newSourceModule, setNewSourceModule] = useState('');
  const [sourceEntityType, setSourceEntityType] = useState('');
  const [trimType, setTrimType] = useState('PERMISSION');
  const [requiredPermission, setRequiredPermission] = useState('');
  const [enforcement, setEnforcement] = useState('BLOCK');
  const [priority, setPriority] = useState('100');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (sourceModule) params.set('sourceModule', sourceModule);
    apiRequest<{ data: RuleRow[] }>(`/v1/search/acl-trim?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, sourceModule, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    if (!ruleCode.trim() || !ruleName.trim() || !newSourceModule.trim() || !sourceEntityType.trim()) {
      setFormError(t('m39.aclTrim.requiredFields'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest<{ data: RuleRow }>('/v1/search/acl-trim', {
        method: 'POST',
        body: {
          ruleCode: ruleCode.trim(),
          ruleName: ruleName.trim(),
          sourceModule: newSourceModule.trim(),
          sourceEntityType: sourceEntityType.trim(),
          trimType,
          enforcement,
          requiredPermission: requiredPermission.trim() || undefined,
          priority: Number(priority) || 100,
        },
        token,
      });
      setCreated(t('m39.aclTrim.created', { code: ruleCode.trim() }));
      setRuleCode(''); setRuleName(''); setNewSourceModule(''); setSourceEntityType('');
      setRequiredPermission(''); setPriority('100');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFormError(t('m39.aclTrim.notAuthorised'));
      } else {
        setFormError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r: RuleRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/acl-trim/${r.aclTrimRuleId}`, {
        method: 'PATCH',
        body: { isActive: !r.isActive },
        token,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (r: RuleRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/acl-trim/${r.aclTrimRuleId}`, { method: 'DELETE', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="acl-trim-console">
      <h1>{t('m39.aclTrim.title')}</h1>

      <section aria-label="acl-trim-create">
        <h2>{t('m39.aclTrim.newRule')}</h2>
        <form onSubmit={submit} aria-label="rule-form">
          <label>{t('m39.aclTrim.ruleCode')}
            <input aria-label="rule-code" value={ruleCode} onChange={(e) => setRuleCode(e.target.value)} />
          </label>
          <label>{t('m39.aclTrim.ruleName')}
            <input aria-label="rule-name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
          </label>
          <label>{t('m39.common.sourceModule')}
            <input aria-label="source-module" value={newSourceModule} onChange={(e) => setNewSourceModule(e.target.value)} />
          </label>
          <label>{t('m39.aclTrim.entityType')}
            <input aria-label="entity-type" value={sourceEntityType} onChange={(e) => setSourceEntityType(e.target.value)} />
          </label>
          <label>{t('m39.aclTrim.trimType')}
            <select aria-label="trim-type" value={trimType} onChange={(e) => setTrimType(e.target.value)}>
              {TRIM_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m39.aclTrim.requiredPermission')}
            <input aria-label="required-permission" value={requiredPermission} onChange={(e) => setRequiredPermission(e.target.value)} />
          </label>
          <label>{t('m39.aclTrim.enforcement')}
            <select aria-label="enforcement" value={enforcement} onChange={(e) => setEnforcement(e.target.value)}>
              {ENFORCEMENTS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m39.common.priority')}
            <input aria-label="priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </label>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m39.common.saving') : t('m39.aclTrim.createRule')}</Button>
        </form>
        {formError && <p role="alert">{formError}</p>}
        {created && <p aria-label="create-success">{created}</p>}
      </section>

      <section aria-label="acl-trim-filters">
        <label>{t('m39.common.filterByModule')}
          <input aria-label="filter-module" value={sourceModule} onChange={(e) => setSourceModule(e.target.value)} />
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading
        ? <p>{t('common.loading')}</p>
        : (
          <DataTable<RuleRow>
            ariaLabel="rule-table"
            testId="rule-table"
            rowKey={(r) => r.aclTrimRuleId}
            rowAriaLabel={(r) => `rule-${r.ruleCode}`}
            emptyMessage={t('m39.aclTrim.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m39.common.code'), render: (r) => r.ruleCode },
              { key: 'name', header: t('m39.common.name'), render: (r) => r.ruleName },
              { key: 'module', header: t('m39.common.module'), render: (r) => r.sourceModule },
              { key: 'entity', header: t('m39.aclTrim.colEntity'), render: (r) => r.sourceEntityType },
              { key: 'trim', header: t('m39.aclTrim.colTrim'), render: (r) => r.trimType },
              { key: 'permission', header: t('m39.aclTrim.colPermission'), render: (r) => r.requiredPermission ?? '—' },
              { key: 'enforce', header: t('m39.aclTrim.colEnforce'), render: (r) => r.enforcement },
              { key: 'priority', header: t('m39.common.priority'), render: (r) => r.priority },
              { key: 'active', header: t('m39.common.active'), render: (r) => r.isActive ? t('common.yes') : t('common.no') },
              { key: 'actions', header: t('m39.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`toggle-${r.ruleCode}`} onClick={() => toggleActive(r)}>
                    {r.isActive ? t('m39.common.disable') : t('m39.common.enable')}
                  </Button>
                  <Button variant="danger" type="button" aria-label={`delete-${r.ruleCode}`} onClick={() => remove(r)}>{t('common.delete')}</Button>
                </>
              ) },
            ]}
          />
        )}
    </main>
  );
}
